import { Injectable } from '@angular/core';
import { CountriesService } from './countries.service';
import { BehaviorSubject, Observable, of, tap, catchError } from 'rxjs';
import { Country } from '../interfaces/country';

@Injectable({
  providedIn: 'root'
})
export class CountrySelectionService {
  private countries: Country[] = [];
  private countriesLoaded = false;
  private loadingCountries = false;
  private countriesSubject = new BehaviorSubject<Country[]>([]);
  public countries$ = this.countriesSubject.asObservable();
  
  private selectedCountrySubject = new BehaviorSubject<Country | null>(null);
  public selectedCountry$ = this.selectedCountrySubject.asObservable();

  constructor(private countriesService: CountriesService) {
    // Cargar países al inicio
    this.loadCountries();
    // Intentar cargar el país seleccionado desde localStorage al inicio
    this.initializeSelectedCountry();
  }

  /**
   * Carga los países desde el servicio
   */
  private loadCountries(): void {
    if (this.loadingCountries) return;
    
    this.loadingCountries = true;
    this.countriesService.getCountries().pipe(
      tap(countries => {
        this.countries = countries;
        this.countriesLoaded = true;
        this.loadingCountries = false;
        this.countriesSubject.next(countries);
        
        // Después de cargar los países, validar el país seleccionado
        this.validateSelectedCountry();
      }),
      catchError(error => {
        console.error('Error al cargar países:', error);
        this.loadingCountries = false;
        return of([]);
      })
    ).subscribe();
  }

  /**
   * Inicializa el país seleccionado desde localStorage
   */
  private initializeSelectedCountry(): void {
    const savedIso = localStorage.getItem('selectedCountryIso');
    if (savedIso) {
      // Crear un país temporal para el guard
      const tempCountry: Country = {
        id: 0,
        name: 'Cargando...',
        isoCode: savedIso
      };
      
      // Establecer el país temporal
      this.selectedCountrySubject.next(tempCountry);
      
      // Si los países ya están cargados, validar inmediatamente
      if (this.countriesLoaded) {
        this.validateSelectedCountry();
      }
      // Si no, se validará cuando se carguen los países
    }
  }
  
  /**
   * Valida el país seleccionado contra la lista de países cargados
   */
  private validateSelectedCountry(): void {
    const savedIso = localStorage.getItem('selectedCountryIso');
    if (!savedIso) return;
    
    const country = this.countries.find(c => c.isoCode === savedIso);
    if (!country) {
      console.warn('País no encontrado en la lista cargada:', savedIso);
      // Eliminar el país guardado si no existe en la lista
      this.clearSelectedCountry();
    } else {
      // Actualizar con el objeto completo
      this.selectedCountrySubject.next(country);
    }
  }

  /**
   * Asegura que la lista de países esté cargada
   */
  ensureCountriesLoaded(): Observable<Country[]> {
    // Si ya están cargados, retornar la lista actual
    if (this.countriesLoaded) {
      return of(this.countries);
    }
    
    // Si está en proceso de carga, esperar
    if (this.loadingCountries) {
      return this.countries$;
    }
    
    // Cargar países desde el servicio
    this.loadCountries();
    return this.countries$;
  }

  /**
   * Verifica si un ISO de país es válido
   */
  isValidCountry(iso: string): boolean {
    // Si los países no están cargados, asumir que sí es válido
    // y programar una validación asíncrona
    if (!this.countriesLoaded) {
      return true; // Permitir navegación mientras carga
    }
    
    return this.countries.some(country => country.isoCode === iso);
  }

  /**
   * Establece un nuevo país seleccionado
   */
  setSelectedCountry(countryIso: string): void {
    // Validar primero que sea un país válido
    this.ensureCountriesLoaded().subscribe(() => {
      const country = this.countries.find(c => c.isoCode === countryIso);
      if (country) {
        localStorage.setItem('selectedCountryIso', countryIso);
        this.selectedCountrySubject.next(country);
      }
    });
  }

  /**
   * Establece directamente un país ya validado
   */
  setSelectedCountryObject(country: Country): void {
    localStorage.setItem('selectedCountryIso', country.isoCode);
    this.selectedCountrySubject.next(country);
    
    // Asegurar que este país esté en nuestra lista en memoria
    if (!this.countriesLoaded || !this.countries.some(c => c.isoCode === country.isoCode)) {
      this.ensureCountriesLoaded().subscribe();
    }
  }

  /**
   * Obtiene el ISO de país seleccionado
   */
  getSelectedCountryIso(): string | null {
    return localStorage.getItem('selectedCountryIso');
  }

  /**
   * Limpia la selección de país
   */
  clearSelectedCountry(): void {
    localStorage.removeItem('selectedCountryIso');
    this.selectedCountrySubject.next(null);
  }
}